import { format, startOfWeek } from 'date-fns';

const MS_PER_HOUR = 3600000;

export function weekKeyMonday(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return format(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

export function msToHours(ms) {
  if (ms == null || Number.isNaN(ms)) return 0;
  return Math.round((Number(ms) / MS_PER_HOUR) * 100) / 100;
}

/** Distinct source datacenter values for filter dropdowns. */
export function collectSourceDatacenters(migrations) {
  const s = new Set();
  for (const m of migrations || []) {
    const dc = String(m.sourceDatacenter || '').trim();
    if (dc) s.add(dc);
  }
  return [...s].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Distinct target datacenter values for filter dropdowns. */
export function collectTargetDatacenters(migrations) {
  const s = new Set();
  for (const m of migrations || []) {
    const dc = String(m.targetDatacenter || '').trim();
    if (dc) s.add(dc);
  }
  return [...s].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * Client-side filters for the main migration workflow table.
 * @param {object} f - dateFromMs/dateToMs (epoch ms), status, sourceDc, targetDc, clientSearch, min/max duration hours, min/max attempts
 */
export function filterMigrationsForTable(migrations, f) {
  if (!f) return migrations || [];
  const clientQ = String(f.clientSearch || '')
    .trim()
    .toLowerCase();
  const src = String(f.sourceDc || '').trim();
  const tgt = String(f.targetDc || '').trim();
  const minH = f.minDurationHours === '' || f.minDurationHours == null ? null : Number(f.minDurationHours);
  const maxH = f.maxDurationHours === '' || f.maxDurationHours == null ? null : Number(f.maxDurationHours);
  const minA = f.minAttempts === '' || f.minAttempts == null ? null : Number(f.minAttempts);
  const maxA = f.maxAttempts === '' || f.maxAttempts == null ? null : Number(f.maxAttempts);
  const dateFromMs = f.dateFromMs != null ? Number(f.dateFromMs) : null;
  const dateToMs = f.dateToMs != null ? Number(f.dateToMs) : null;
  const status = f.status || 'all';

  return (migrations || []).filter((row) => {
    if (clientQ && !String(row.clientName || '').toLowerCase().includes(clientQ)) return false;
    if (src && String(row.sourceDatacenter || '') !== src) return false;
    if (tgt && String(row.targetDatacenter || '') !== tgt) return false;

    const created = row.latestRunCreatedAt || (Array.isArray(row.attempts) && row.attempts[0]?.runCreatedAt);
    const createdMs = created ? new Date(created).getTime() : null;
    if (dateFromMs != null && Number.isFinite(dateFromMs)) {
      if (createdMs == null || createdMs < dateFromMs) return false;
    }
    if (dateToMs != null && Number.isFinite(dateToMs)) {
      if (createdMs == null || createdMs > dateToMs) return false;
    }

    const st = row.latestStatus;
    const conc = row.latestConclusion;
    if (status !== 'all') {
      switch (status) {
        case 'success':
          if (st !== 'completed' || conc !== 'success') return false;
          break;
        case 'failure':
          if (st !== 'completed' || conc !== 'failure') return false;
          break;
        case 'cancelled':
          if (st !== 'completed' || conc !== 'cancelled') return false;
          break;
        case 'skipped':
          if (st !== 'completed' || conc !== 'skipped') return false;
          break;
        case 'not_completed':
          if (st === 'completed') return false;
          break;
        default:
          break;
      }
    }

    const ms = totalDurationMs(row);
    const hours = ms / 3600000;
    if (minH != null && !Number.isNaN(minH) && hours < minH) return false;
    if (maxH != null && !Number.isNaN(maxH) && hours > maxH) return false;

    const ac = row.attemptCount != null ? Number(row.attemptCount) : (row.attempts || []).length;
    if (minA != null && !Number.isNaN(minA) && ac < minA) return false;
    if (maxA != null && !Number.isNaN(maxA) && ac > maxA) return false;

    return true;
  });
}

/** Latest attempt by startedAt (for week bucket). */
function getLatestAttempt(migration) {
  const attempts = migration.attempts || [];
  if (attempts.length === 0) return null;
  let latest = attempts[0];
  for (const a of attempts) {
    const t = new Date(a.startedAt || a.runCreatedAt || 0).getTime();
    const lt = new Date(latest.startedAt || latest.runCreatedAt || 0).getTime();
    if (t >= lt) latest = a;
  }
  return latest;
}

function totalDurationMs(migration) {
  return (migration.attempts || []).reduce((s, a) => s + (Number(a.durationMs) || 0), 0);
}


function matchesAttemptCountFilter(n, filter) {
  if (filter === 'any' || filter == null || filter === '') return true;
  if (filter === '5+') return n >= 5;
  const want = parseInt(filter, 10);
  if (Number.isNaN(want)) return true;
  return n === want;
}

/**
 * Weekly trend: client migration paths matching filters.
 * - maxHours: total duration (sum of attempts) must be strictly under this many hours
 * - sourceDcFilter: optional source DC (case-insensitive)
 * - attemptCountFilter: 'any' | '1' | '2' | '3' | '4' | '5+' (exact count, or >=5 for 5+)
 *
 * Week = Monday week of the latest attempt's start (or runCreatedAt).
 * Returns [{ week, clientCount, items: [...] }, ...]
 */
export function buildWeeklyFilteredClientTrend(migrations, maxHours, sourceDcFilter, attemptCountFilter, fromMs = null, toMs = null) {
  const n = Number(maxHours);
  const maxMs = (Number.isFinite(n) && n > 0 ? n : 3) * MS_PER_HOUR;
  const dcFilter = sourceDcFilter != null ? String(sourceDcFilter).trim() : '';
  const dcFilterLower = dcFilter ? dcFilter.toLowerCase() : '';
  const acf = attemptCountFilter === undefined || attemptCountFilter === null ? '1' : attemptCountFilter;

  const weekMap = new Map();

  for (const m of migrations || []) {
    const attempts = m.attempts || [];
    const ac = attempts.length;
    if (ac === 0) continue;
    if (!matchesAttemptCountFilter(ac, acf)) continue;

    const sumMs = totalDurationMs(m);
    if (Number.isNaN(sumMs) || sumMs >= maxMs) continue;

    if (dcFilterLower) {
      const sdc = String(m.sourceDatacenter || '').trim().toLowerCase();
      if (sdc !== dcFilterLower) continue;
    }

    const latest = getLatestAttempt(m);
    const latestMs = latest ? new Date(latest.startedAt || latest.runCreatedAt).getTime() : NaN;
    if (!Number.isFinite(latestMs)) continue;
    if (fromMs != null && latestMs < fromMs) continue;
    if (toMs != null && latestMs > toMs) continue;

    const wk = weekKeyMonday(latest?.startedAt || latest?.runCreatedAt);
    if (!wk) continue;

    if (!weekMap.has(wk)) {
      weekMap.set(wk, []);
    }

    const latestUrl = latest?.htmlUrl || null;
    weekMap.get(wk).push({
      clientName: m.clientName || '—',
      sourceDatacenter: m.sourceDatacenter || '—',
      targetDatacenter: m.targetDatacenter || '—',
      attemptCount: ac,
      totalDurationMs: sumMs,
      latestHtmlUrl: latestUrl,
      latestStatus: m.latestStatus ?? null,
      latestConclusion: m.latestConclusion ?? null
    });
  }

  return [...weekMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([week, items]) => ({
      week,
      clientCount: items.length,
      items
    }));
}

/**
 * Weekly trend with no duration / attempt / source-DC filters — every migration path with attempts,
 * bucketed by latest-attempt week (for comparison against {@link buildWeeklyFilteredClientTrend}).
 */
export function buildWeeklyAllClientTrend(migrations, fromMs = null, toMs = null) {
  const weekMap = new Map();

  for (const m of migrations || []) {
    const attempts = m.attempts || [];
    if (attempts.length === 0) continue;

    const ac = attempts.length;
    const sumMs = totalDurationMs(m);
    const latest = getLatestAttempt(m);
    const latestMs = latest ? new Date(latest.startedAt || latest.runCreatedAt).getTime() : NaN;
    if (!Number.isFinite(latestMs)) continue;
    if (fromMs != null && latestMs < fromMs) continue;
    if (toMs != null && latestMs > toMs) continue;

    const wk = weekKeyMonday(latest?.startedAt || latest?.runCreatedAt);
    if (!wk) continue;

    if (!weekMap.has(wk)) {
      weekMap.set(wk, []);
    }

    const latestUrl = latest?.htmlUrl || null;
    weekMap.get(wk).push({
      clientName: m.clientName || '—',
      sourceDatacenter: m.sourceDatacenter || '—',
      targetDatacenter: m.targetDatacenter || '—',
      attemptCount: ac,
      totalDurationMs: sumMs,
      latestHtmlUrl: latestUrl,
      latestStatus: m.latestStatus ?? null,
      latestConclusion: m.latestConclusion ?? null
    });
  }

  return [...weekMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([week, items]) => ({
      week,
      clientCount: items.length,
      items
    }));
}

/**
 * Align "all" and "filtered" weekly rows by week for a dual-series line chart.
 */
export function mergeWeeklyTrendChartData(allSeries, filteredSeries) {
  const weeks = new Set();
  for (const x of allSeries || []) {
    if (x?.week) weeks.add(x.week);
  }
  for (const x of filteredSeries || []) {
    if (x?.week) weeks.add(x.week);
  }
  const sorted = [...weeks].sort((a, b) => a.localeCompare(b));
  return sorted.map((week) => {
    const a = (allSeries || []).find((x) => x.week === week);
    const f = (filteredSeries || []).find((x) => x.week === week);
    return {
      week,
      allCount: a?.clientCount ?? 0,
      filteredCount: f?.clientCount ?? 0,
      allItems: a?.items ?? [],
      filteredItems: f?.items ?? []
    };
  });
}
